import React, { useState } from "react";
import { Link } from "react-router-dom";

const STEP_ICONS = {
  profile: "✦",
  photo: "📸",
  post: "💬",
  encounter: "🔥",
};

function ScoreRing({ score }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const color = score >= 75 ? "#22c55e" : score >= 40 ? "#8b6ff5" : "#f97316";

  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
      <svg className="-rotate-90" width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
        <circle
          cx="28" cy="28" r={r}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s ease" }}
        />
      </svg>
      <span className="absolute text-sm font-bold text-white">{score}%</span>
    </div>
  );
}

export default function ReEngagementBanner({ completeness, onDismiss }) {
  const [expanded, setExpanded] = useState(false);

  if (!completeness) return null;

  const { score, steps = [] } = completeness;
  const nextStep = steps.find(s => !s.done);

  // Don't show if perfect score
  if (score >= 100) return null;

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-sensoria-accent/25 bg-gradient-to-br from-sensoria-card via-sensoria-card to-sensoria-accent/5 shadow-lg shadow-sensoria-accent/5">
      {/* Main row */}
      <div className="flex items-center gap-3 p-4">
        <ScoreRing score={score} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">
              {score === 0
                ? "Bienvenue sur Univers Libertin 🎉"
                : score < 50
                  ? "Complète ton profil ✦"
                  : "Tu y es presque !"}
            </h3>
          </div>
          {nextStep && (
            <p className="mt-0.5 truncate text-xs text-sensoria-text-dim">
              Prochaine étape : {nextStep.label}
            </p>
          )}
          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sensoria-accent to-purple-400 transition-all duration-1000"
              style={{ width: `${score}%` }}
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {/* Expand */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="rounded-lg p-2 text-sensoria-muted transition-colors hover:bg-white/5 hover:text-white"
          >
            <svg className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {/* Dismiss */}
          <button
            onClick={onDismiss}
            className="rounded-lg p-2 text-sensoria-muted transition-colors hover:bg-white/5 hover:text-white"
            title="Masquer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded steps */}
      {expanded && (
        <div className="border-t border-sensoria-border/20 px-4 pb-4 pt-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {steps.map((step) => (
              <Link
                key={step.key}
                to={step.done ? "#" : step.path}
                onClick={step.done ? e => e.preventDefault() : undefined}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-all ${
                  step.done
                    ? "border-green-500/20 bg-green-500/5 text-green-400/70 cursor-default"
                    : "border-sensoria-border/30 bg-sensoria-surface/30 text-sensoria-text-dim hover:border-sensoria-accent/30 hover:bg-sensoria-accent/5 hover:text-white"
                }`}
              >
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm ${
                  step.done ? "bg-green-500/15 text-green-400" : "bg-sensoria-accent/10 text-sensoria-accent"
                }`}>
                  {step.done ? "✓" : (STEP_ICONS[step.key] || "→")}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs">{step.label}</span>
                {!step.done && (
                  <svg className="h-3.5 w-3.5 shrink-0 text-sensoria-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </Link>
            ))}
          </div>

          {/* CTA quick action */}
          {nextStep && (
            <Link
              to={nextStep.path}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-sensoria-accent/20 px-4 py-2.5 text-sm font-semibold text-sensoria-accent-light transition-all hover:bg-sensoria-accent/30"
            >
              <span>{STEP_ICONS[nextStep.key] || "→"}</span>
              <span>Continuer : {nextStep.label}</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
